import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import { colors, fonts } from './designSystem';

/**
 * Un panneau qui monte du bas de l'écran, pour les filtres et le tri du
 * listing sur mobile (lot 3, « piste B » validée par Clément le 22/09).
 *
 * Rendu dans `document.body` : la barre des filtres est collante et crée son
 * propre empilement, le panneau passerait sous l'en-tête du site. Il n'existe
 * qu'ouvert, donc jamais au rendu serveur.
 */

const Voile = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(20, 30, 40, 0.45);
`;

const Fenetre = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1001;
  display: flex;
  flex-direction: column;
  max-height: 85vh;
  border-radius: 20px 20px 0 0;
  background: ${colors.white};
  font-family: ${fonts.body};
  color: ${colors.textDark};
  box-shadow: 0 -4px 24px rgba(49, 73, 89, 0.18);
`;

const Poignee = styled.span`
  display: block;
  width: 40px;
  height: 4px;
  margin: 8px auto 4px;
  border-radius: 2px;
  background: ${colors.borderLight};
`;

const Tete = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 20px 4px;

  h2 {
    margin: 0;
    font-family: ${fonts.heading};
    font-size: 1.25rem !important;
    color: ${colors.bleuFonce};
  }
`;

const Corps = styled.div`
  flex: 1;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 0 20px 12px;
`;

const Droite = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Fermer = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  margin-right: -8px;
  border: none;
  border-radius: 50%;
  background: none;
  color: ${colors.bleuFonce};
  font-size: 1.6rem;
  line-height: 1;
  cursor: pointer;
`;

const Pied = styled.div`
  padding: 12px 20px calc(16px + env(safe-area-inset-bottom, 0px));
  border-top: 1px solid ${colors.borderLight};
`;

export default function PanneauBas({
  titre,
  action,
  pied,
  onFermer,
  children,
}) {
  const fenetre = useRef(null);

  useEffect(() => {
    const avant = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const precedent = document.activeElement;
    if (fenetre.current) fenetre.current.focus();
    const surTouche = (e) => {
      if (e.key === 'Escape') onFermer();
      // Tab et Maj+Tab restent dans le panneau, comme dans le tunnel.
      if (e.key !== 'Tab' || !fenetre.current) return;
      const focusables = [
        ...fenetre.current.querySelectorAll(
          'a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])',
        ),
      ];
      if (!focusables.length) return;
      const premier = focusables[0];
      const dernier = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === premier) {
        e.preventDefault();
        dernier.focus();
      } else if (!e.shiftKey && document.activeElement === dernier) {
        e.preventDefault();
        premier.focus();
      } else if (!fenetre.current.contains(document.activeElement)) {
        e.preventDefault();
        premier.focus();
      }
    };
    document.addEventListener('keydown', surTouche);
    return () => {
      document.body.style.overflow = avant;
      document.removeEventListener('keydown', surTouche);
      if (precedent && precedent.focus) precedent.focus();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return ReactDOM.createPortal(
    <>
      <Voile onClick={onFermer} />
      <Fenetre
        ref={fenetre}
        role="dialog"
        aria-modal="true"
        aria-label={titre}
        tabIndex={-1}>
        <Poignee aria-hidden="true" />
        <Tete>
          <h2>{titre}</h2>
          <Droite>
            {action}
            <Fermer type="button" aria-label="Fermer" onClick={onFermer}>
              <span aria-hidden="true">&times;</span>
            </Fermer>
          </Droite>
        </Tete>
        <Corps>{children}</Corps>
        {pied && <Pied>{pied}</Pied>}
      </Fenetre>
    </>,
    document.body,
  );
}
